// THE form renderer. The builder's test mode and the public form page render
// through this one component, so the builder preview cannot diverge from what
// respondents see. Sections, cards, row packing, repeatable widget instances
// (nested, min/max-clamped, per-instance rules) all come from the stored
// definition — nothing here is specific to any one form.
import { useMemo } from 'react';
import type { FieldDefinition, FormCard, FormConfig, FormRequiredStyle, FormSection, FormUploadSettings, WidgetNode } from '../shared/types';
import { fieldLocked, fieldRequired } from './config';
import { calcNote } from './rules';
import {
  FormState, InstancePath, PublishedDef, WidgetInstance, addInstance, removeInstance, setFiles, setSignatureMode, setValue, valueKey, widgetForSection,
} from './model';
import { CtxEval, FormEval, evaluateForm, renderableIn } from './evaluate';
import { cardsFor, childrenOf, cardOrder, groupIn } from './cards';
import { boxStyle, hasBackground, instancePadding, sectionBodyStyle, sectionWrapStyle } from './style';
import FieldInput from './FieldInput';
import { RichBlocks } from './richTextView';
import type { FieldErrors, ValidationError } from './payload';
import { STRINGS as S } from './strings';

const byOrder = (a: { sortOrder?: number }, b: { sortOrder?: number }) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0);

/** Group consecutive fields sharing a non-empty ui_meta.row into one visual row. */
function groupRows(list: FieldDefinition[]): FieldDefinition[][] {
  const runs: FieldDefinition[][] = [];
  for (const f of list) {
    const r = (f.ui_meta?.row || '').trim();
    const last = runs[runs.length - 1];
    const lastR = last ? (last[0].ui_meta?.row || '').trim() : '';
    if (r && last && lastR === r) last.push(f);
    else runs.push([f]);
  }
  return runs;
}

export type StateUpdater = (update: (s: FormState) => FormState) => void;

export interface FormRendererProps {
  def: PublishedDef;
  state: FormState;
  onState: StateUpdater;
  cfg?: FormConfig;
  /** distinct DOM-id prefix when several renderers coexist (builder preview) */
  idPrefix?: string;
  /** render everything disabled (submission detail view) */
  readOnly?: boolean;
  /** render only these sections (already sorted) — the pager's page filter */
  sections?: FormSection[];
  /**
   * Fields filled from the respondent's signed-in account. Locked for the same
   * reason a Logic "locked mirror" is: the value is not theirs to change. The
   * server re-applies these at submit, so this is presentation, not the rule.
   */
  accountLocked?: { fields: string[]; providerName?: string; reason?: 'account' | 'locked' };
  /**
   * What the link that opened the form filled in (P1): `locked` keys came
   * from a signed link and render disabled; `prefilled` keys are editable
   * defaults an unsigned link supplied. Either may name a composite part.
   */
  link?: { locked: string[]; prefilled: string[] };
  /**
   * Answers carried over from the submission this form continues (the order handoff).
   * `locked` ones arrive in `accountLocked` too, which is what disables them;
   * `confirm` ones render a verdict control the person has to answer.
   */
  carried?: CarriedProps;
  /**
   * Editing after submit (P6): file fields already answered, by key, with a
   * description of what was given ("a.pdf, b.png"). Rendered as a note under
   * the control — the files themselves stay where they are unless replaced.
   */
  provided?: Record<string, string>;
  /** The form's name — the {{formName}} token in the terms above a signature pad. */
  formName?: string;
  /**
   * Signature fields render as what was signed, with no pad and no "Sign
   * again": the admin edit drawer. A signature is made with the terms in
   * front of the signer and a record of when; an edit has neither, and the
   * server refuses a drawn one anyway — so the control is not offered.
   */
  signaturesLocked?: boolean;
  /**
   * What is wrong, per control, keyed like `ValidationError.path` — the
   * value key (`inst.key` inside a widget) with a dotted part for one
   * sub-input of a composite. Set by the pager after a failed Next or submit.
   */
  errors?: FieldErrors;
  /** an answer changed — drop its error (the path is what `errors` is keyed by) */
  onClearError?: (path: string) => void;
  /** a control refused something itself (a file over its limit): add the error to the map */
  onFieldError?: (error: ValidationError) => void;
  /** the form's upload settings — what a 'file' field's own `ui_meta.upload` sits over */
  uploads?: FormUploadSettings;
  /** how required / optional fields are marked (`settings.requiredStyle`); absent = asterisk + "(optional)" */
  requiredStyle?: FormRequiredStyle;
}

/** The carried block's values and the verdicts given so far. */
export interface CarriedProps {
  fields: Record<string, { from: unknown; mode: 'locked' | 'confirm' | 'editable' }>;
  confirmations: Record<string, boolean>;
  onConfirm: (key: string, correct: boolean) => void;
  /** true once the person has tried to submit — unanswered verdicts go red */
  showErrors?: boolean;
}

interface Rctx {
  def: PublishedDef;
  ev: FormEval;
  cfg: FormConfig;
  onState: StateUpdater;
  idPrefix: string;
  readOnly: boolean;
  state: FormState;
  accountLocked?: { fields: string[]; providerName?: string; reason?: 'account' | 'locked' };
  link?: { locked: string[]; prefilled: string[] };
  carried?: CarriedProps;
  provided?: Record<string, string>;
  formName?: string;
  signaturesLocked?: boolean;
  errors: FieldErrors;
  onClearError?: (path: string) => void;
  onFieldError?: (error: ValidationError) => void;
  uploads?: FormUploadSettings;
  requiredStyle?: FormRequiredStyle;
}

export function useFormEval(def: PublishedDef, state: FormState, cfg: FormConfig): FormEval {
  return useMemo(() => evaluateForm(def, state, cfg), [def, state, cfg]);
}

const NO_ERRORS: FieldErrors = {};

export default function FormRenderer({ def, state, onState, cfg = {}, idPrefix = 'f', readOnly = false, sections: only, accountLocked, link, carried, provided, formName, signaturesLocked, errors = NO_ERRORS, onClearError, onFieldError, uploads, requiredStyle }: FormRendererProps) {
  const ev = useFormEval(def, state, cfg);
  const r: Rctx = { def, ev, cfg, onState, idPrefix, readOnly, state, accountLocked, link, carried, provided, formName, signaturesLocked, errors, onClearError, onFieldError, uploads, requiredStyle };
  const sections = only ?? (def.layout?.sections || []).slice().sort(byOrder);
  return <>{sections.map((s) => <SectionView key={s.id} section={s} r={r} />)}</>;
}

/**
 * A section's background paints its BODY — the card the questions sit in —
 * the way a repeatable container's paints its instance card: the heading stays
 * above the colour, on the page, and there is no second well inside the box.
 */
const sectionWrap = (section: FormSection): React.CSSProperties =>
  sectionWrapStyle(section.style);
const sectionBody = (section: FormSection): React.CSSProperties =>
  sectionBodyStyle(section.style);

function SectionView({ section, r }: { section: FormSection; r: Rctx }) {
  if (section.showFlag && !r.cfg[section.showFlag]) return null;
  if (section.hideWith && r.cfg[section.hideWith]) return null;
  // Section-level logic ('sec:…' rule targets): the whole section vanishes,
  // fields inside are already in the hidden sets so nothing in it is required.
  if (r.ev.hiddenSections.has(section.id)) return null;
  if (section.type === 'widget' || section.type === 'locations') return <WidgetSectionView section={section} r={r} />;
  // 'fields' — and legacy 'totals' / 'files' sections render as plain field
  // sections too (their fields are ordinary catalog fields in the new model).
  return <FieldsSectionView section={section} r={r} />;
}

// ---- cards -----------------------------------------------------------------
/**
 * What one container renders, in document order: runs of fields and the cards
 * dropped between them.
 *
 * The same walk serves a plain section (cards with no scope, answered once), a
 * repeatable widget node (cards carrying that node's scope, answered once per
 * instance) and a card inside either of those — one shape means a card looks
 * and behaves the same wherever it was dragged.
 */
type Block =
  | { t: 'fields'; items: FieldDefinition[] }
  | { t: 'card'; card: FormCard; blocks: Block[] }
  /** a repeatable container nested in this one, at the place it was dropped */
  | { t: 'node'; node: WidgetNode };

const hasContent = (blocks: Block[]): boolean =>
  blocks.some((b) => (b.t === 'fields' ? b.items.length > 0 : b.t === 'node' ? true : hasContent(b.blocks)));

/** Containers published before one could be placed still come last. */
const LEGACY_LAST = Number.MAX_SAFE_INTEGER / 2;
const nodeOrder = (n: WidgetNode, i: number): number =>
  (typeof n.order === 'number' && Number.isFinite(n.order) ? n.order : LEGACY_LAST + i);

/**
 * `nodes` are the containers nested directly in this one. They sit in the
 * container's BODY only — a card groups questions, not repeaters — so the
 * recursion into a card never carries them.
 */
function blocksIn(
  sectionId: string, cards: FormCard[], fields: FieldDefinition[], parent: string,
  ctx: CtxEval, renderable: (f: FieldDefinition) => boolean, nodes: WidgetNode[] = []
): Block[] {
  const mine = fields.filter((f) => groupIn(cards, f) === parent && renderable(f));
  const kids = childrenOf(cards, parent).filter((c) => !ctx.hiddenCards.has(`${sectionId}:${c.id}`));
  const seq: { order: number; block: Block }[] = [
    ...mine.map((f) => ({ order: f.sort_order ?? 0, block: { t: 'fields' as const, items: [f] } })),
    ...kids.map((c) => ({
      order: cardOrder(c),
      block: { t: 'card' as const, card: c, blocks: blocksIn(sectionId, cards, fields, c.id, ctx, renderable) },
    })),
    ...nodes.map((n, i) => ({ order: nodeOrder(n, i), block: { t: 'node' as const, node: n } })),
  ];
  // A card with nothing left in it (every field hidden, every child card gone)
  // would render as an empty box under a heading, so it is dropped.
  const kept = seq
    .filter((x) => x.block.t !== 'card' || hasContent(x.block.blocks))
    .sort((a, b) => a.order - b.order);
  // Adjacent fields coalesce into one run so row packing sees the whole of it.
  const out: Block[] = [];
  for (const { block } of kept) {
    const last = out[out.length - 1];
    if (block.t === 'fields' && last && last.t === 'fields') last.items.push(...block.items);
    else out.push(block.t === 'fields' ? { t: 'fields', items: [...block.items] } : block);
  }
  return out;
}

function Blocks({ blocks, depth, ctx, path, instanceId, r, nested }: {
  blocks: Block[]; depth: number;
  ctx: CtxEval; path: InstancePath; instanceId: string | null; r: Rctx;
  /** how to draw a nested container — only a widget body ever passes one */
  nested?: (node: WidgetNode) => React.ReactNode;
}) {
  return (
    <>
      {blocks.map((b, i) => {
        if (b.t === 'fields') {
          return <FieldRows key={`r${i}`} fields={b.items} ctx={ctx} path={path} instanceId={instanceId} r={r} />;
        }
        // A container this instance hides is gone for this instance only —
        // the same per-instance reading a card inside a widget gets.
        if (b.t === 'node') {
          if (ctx.hiddenScopes.has(b.node.scope)) return null;
          return nested ? <div key={b.node.scope}>{nested(b.node)}</div> : null;
        }
        return (
          <div key={b.card.id}>
            {b.card.title ? <div className={`of-subsection-title d${Math.min(depth, 2)}`}>{b.card.title}</div> : null}
            <div className={`of-subcard d${Math.min(depth, 2)}`} style={boxStyle(b.card.style, { padding: 20 })}>
              <Blocks blocks={b.blocks} depth={depth + 1} ctx={ctx} path={path} instanceId={instanceId} r={r} />
            </div>
          </div>
        );
      })}
    </>
  );
}

// ---- plain field sections (order scope) ------------------------------------
function FieldsSectionView({ section, r }: { section: FormSection; r: Rctx }) {
  const { def, ev, cfg } = r;
  const secFields = def.fields.filter((f) => f.scope === 'order' && f.section === section.id);
  const renderable = (f: FieldDefinition) => renderableIn(f, ev.order, cfg, ev);
  const blocks = blocksIn(section.id, cardsFor(section, 'order'), secFields, '', ev.order, renderable);
  if (!hasContent(blocks)) return null;
  return (
    <div className="of-section" style={sectionWrap(section)}>
      <h2 className="of-section-title">{section.title}</h2>
      {section.desc ? <RichBlocks className="of-section-desc" text={section.desc} /> : null}
      <div className="of-section-card" style={sectionBody(section)}>
        <Blocks blocks={blocks} depth={0} ctx={ev.order} path={[]} instanceId={null} r={r} />
      </div>
    </div>
  );
}

// ---- shared field-row rendering (any context) ------------------------------
function FieldRows({ fields, ctx, path, instanceId, r }: {
  fields: FieldDefinition[]; ctx: CtxEval; path: InstancePath; instanceId: string | null; r: Rctx;
}) {
  return (
    <>
      {groupRows(fields).map((run) => (run.length === 1
        ? <OneField key={run[0].key} def={run[0]} ctx={ctx} path={path} instanceId={instanceId} r={r} />
        : (
          <div key={run[0].key} className={`of-row-${Math.min(run.length, 4)}`}>
            {run.map((f) => <OneField key={f.key} def={f} ctx={ctx} path={path} instanceId={instanceId} r={r} />)}
          </div>
        )))}
    </>
  );
}

function OneField({ def: f, ctx, path, instanceId, r }: {
  def: FieldDefinition; ctx: CtxEval; path: InstancePath; instanceId: string | null; r: Rctx;
}) {
  const { def, ev, cfg, onState, readOnly } = r;
  const domId = `${r.idPrefix}-${instanceId || 'order'}-${f.key}`;

  // A field something SETS renders read-only with its computed value. A rule
  // that is not holding right now clears its target rather than leaving the
  // last number it worked out, so an empty box here is the honest answer — not
  // a zero standing in for one.
  const isCalcTarget = ev.calcs[f.key] !== undefined;
  if (isCalcTarget) {
    const computed = ev.calcs[f.key];
    const numeric = f.input_type === 'number' && computed !== '';
    // A choice question keeps a value ("yes") and shows a label ("Yes"). What
    // is submitted is the value either way — it comes off `ev.calcs`, not off
    // this box — but the respondent is owed the word the question uses.
    const asOption = f.options?.find((o) => o.value === String(computed ?? ''))?.text;
    return (
      <div className="of-group">
        <label htmlFor={domId}>{f.label}</label>
        <input
          id={domId}
          type={numeric ? 'number' : 'text'}
          value={numeric ? Number(computed) || 0 : asOption ?? String(computed ?? '')}
          disabled readOnly
        />
        <p className="of-info of-note">{S.note.computed(calcNote(def.rules, f.key))}</p>
      </div>
    );
  }

  const lockedFrom = ctx.locked[f.key];
  const prefilledFrom = ctx.prefilled[f.key];
  const fromAccount = r.accountLocked?.fields.includes(f.key);
  // Read-only: Build's own Locked answer, or a rule holding it that way. Unlike
  // a mirror it writes nothing — whatever answer is there stays there, it is
  // just not theirs to change now.
  const isLocked = fieldLocked(f, ctx);
  // Prefill rules can also target a composite's PARTS ('addr.company') — those
  // sub-inputs render disabled while the rest of the field stays editable.
  const subPrefix = `${f.key}.`;
  const subOf = (keys: string[]) => keys.filter((k) => k.startsWith(subPrefix)).map((k) => k.slice(subPrefix.length));
  // …and so can a signed link (P1), for the same treatment.
  const fromLink = instanceId ? false : !!r.link?.locked.includes(f.key);
  const linkSubs = instanceId ? [] : subOf(r.link?.locked || []);
  const linkFilled = !instanceId && !!r.link && (r.link.prefilled.includes(f.key) || subOf(r.link.prefilled).length > 0);
  const lockedSubs = [...subOf(Object.keys(ctx.locked)), ...linkSubs];
  const subPrefilled = Object.keys(ctx.prefilled).some((k) => k.startsWith(subPrefix));
  const labelOf = (k: string) => def.fields.find((x) => x.key === k)?.label || k;
  // A carried answer is only carried at ORDER scope: the mapping names one
  // field on the parent submission, and a repeating instance has no counterpart
  // to be carried from.
  const carriedHere = instanceId ? undefined : r.carried?.fields[f.key];
  // A file already given on the submission being edited (P6).
  const providedHere = instanceId ? undefined : r.provided?.[f.key];
  const note = providedHere
    ? S.edit.provided(providedHere)
    : carriedHere
    ? (carriedHere.mode === 'locked'
      ? S.note.carriedLocked
      : carriedHere.mode === 'confirm'
        ? S.note.carriedConfirm
        : S.note.carriedEditable)
    : fromAccount
    ? (r.accountLocked?.reason === 'locked' ? S.note.locked : S.note.fromAccount(r.accountLocked?.providerName || S.note.signedIn))
    : fromLink ? S.note.fromLink
    : lockedFrom
      // A locked prefill of a FIXED value has no source answer to name: the
      // rule carries the value itself, so the note says so rather than
      // pointing at the field's own label as if it mirrored itself.
      ? (ctx.fixed.has(f.key) ? S.note.setByForm : S.note.mirrored(labelOf(lockedFrom)))
      : isLocked ? S.note.locked
      : prefilledFrom && ctx.values[f.key] ? S.note.prefilled
      : linkSubs.length ? S.note.partsFromLink
      : lockedSubs.length ? S.note.partsMirrored
      : linkFilled && ctx.values[f.key] ? S.note.prefilled
      : subPrefilled ? S.note.partsPrefilled : undefined;

  // This control's errors: its own, and — for a composite — one per part.
  const vk = valueKey(instanceId, f.key);
  const error = r.errors[vk];
  const subErrors: Record<string, string> = {};
  for (const k of Object.keys(r.errors)) if (k.startsWith(`${vk}.`)) subErrors[k.slice(vk.length + 1)] = r.errors[k];

  const input = (
    <FieldInput
      def={f} cfg={cfg}
      domId={domId}
      value={ctx.values[f.key]}
      ctxValues={ctx.values}
      formName={r.formName}
      signaturesLocked={r.signaturesLocked}
      lockedSubs={lockedSubs}
      required={fieldRequired(f, cfg, ctx)}
      disabled={readOnly || !!lockedFrom || !!fromAccount || fromLink || isLocked}
      note={note}
      onChange={(v) => onState((s) => setValue(s, path, f.key, v))}
      files={r.state.files[vk]}
      onFiles={(files) => onState((s) => setFiles(s, instanceId, f.key, files))}
      uploads={r.uploads}
      requiredStyle={r.requiredStyle}
      onSetError={r.onFieldError ? (message) => r.onFieldError!({ key: f.key, label: f.label, where: '', message, path: vk }) : undefined}
      onSignatureMode={(mode) => onState((s) => setSignatureMode(s, instanceId, f.key, mode))}
      signatureMode={r.state.signatures[vk]}
      error={error}
      subErrors={subErrors}
      errorPath={vk}
      onClearError={r.onClearError ? (sub) => r.onClearError!(sub ? `${vk}.${sub}` : vk) : undefined}
    />
  );

  if (carriedHere?.mode !== 'confirm' || !r.carried || readOnly) return input;
  return (
    <>
      {input}
      <ConfirmCarried fieldKey={f.key} label={f.label} carried={r.carried} />
    </>
  );
}

/**
 * "Is this right?" beside a carried-over answer.
 *
 * Deliberately not inferred from whether the value changed. Somebody who read
 * the billing contact and agreed, and somebody who never looked at it, leave
 * identical answers behind — and only one of them has confirmed anything. The
 * business later relies on the difference.
 */
function ConfirmCarried({ fieldKey, label, carried }: {
  fieldKey: string; label: string; carried: CarriedProps;
}) {
  const given = carried.confirmations[fieldKey];
  const unanswered = typeof given !== 'boolean';
  const name = `confirm-${fieldKey}`;
  return (
    <div
      className="of-confirm"
      role="radiogroup"
      aria-label={S.confirm.group(label)}
      aria-invalid={carried.showErrors && unanswered ? true : undefined}
      style={{
        display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap',
        margin: '-6px 0 14px', fontSize: 13,
        color: carried.showErrors && unanswered ? '#c53030' : '#4a5568',
      }}
    >
      <span>{S.confirm.ask}</span>
      <label style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
        <input
          type="radio" name={name} checked={given === true}
          onChange={() => carried.onConfirm(fieldKey, true)}
        /> {S.confirm.yes}
      </label>
      <label style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
        <input
          type="radio" name={name} checked={given === false}
          onChange={() => carried.onConfirm(fieldKey, false)}
        /> {S.confirm.no}
      </label>
      {carried.showErrors && unanswered && <span>{S.confirm.missing}</span>}
    </div>
  );
}

// ---- widget sections (repeatable instance trees) ---------------------------
function WidgetSectionView({ section, r }: { section: FormSection; r: Rctx }) {
  const { def } = r;
  const widget = widgetForSection(def.widgets, section);
  if (!widget?.tree) return null;
  // A hidden root container takes its section's heading with it: a title over
  // an empty box is not what "hide this" means.
  if (r.ev.order.hiddenScopes.has(widget.tree.scope)) return null;
  const instances = <WidgetInstances node={widget.tree} section={section} parentPath={[]} depth={0} r={r} />;
  return (
    <div className="of-section" style={sectionWrap(section)}>
      <h2 className="of-section-title">{section.title}</h2>
      {section.desc ? <RichBlocks className="of-section-desc" text={section.desc} /> : null}
      {/* A widget section has no body card of its own until it is given a
          colour — then it gets the one every other section has, so the whole
          run of instances sits on the background as one box. An unstyled
          section renders exactly the markup it always did. */}
      {hasBackground(section.style)
        ? <div className="of-section-card" style={sectionBody(section)}>{instances}</div>
        : instances}
    </div>
  );
}

/** The recursive heart: renders every instance of `node` under `parentPath`. */
function WidgetInstances({ node, section, parentPath, depth, r }: {
  node: WidgetNode; section: FormSection; parentPath: InstancePath; depth: number; r: Rctx;
}) {
  const { def, ev, cfg, onState, readOnly, state } = r;
  // Resolve the instance list at this path.
  let list: WidgetInstance[] = [];
  if (!parentPath.length) list = state.widgets[node.scope] || [];
  else {
    let inst: WidgetInstance | undefined;
    for (const seg of parentPath) {
      const l: WidgetInstance[] | undefined = inst ? inst.children[seg.scope] : state.widgets[seg.scope];
      inst = l?.[seg.index];
    }
    list = inst?.children[node.scope] || [];
  }

  const nodeFields = def.fields.filter((f) => f.scope === node.scope);
  const canAdd = !readOnly && (node.max === 0 || list.length < node.max);
  const canRemove = !readOnly && list.length > node.min;
  const cardClass = depth === 0 ? 'of-location-card' : depth === 1 ? 'of-device-card' : 'of-line-card';
  // A styled container paints every one of its instance cards, so the box the
  // respondent sees repeated is the box the builder drew.
  const cardStyle = boxStyle(node.style, { padding: instancePadding(depth) });
  // perRow > 1 lays instance cards side by side, wrapping onto further rows.
  const perRow = Math.max(1, Math.min(4, node.perRow || 1));

  // Cards dragged inside this container repeat with it, so they are resolved
  // per instance: one instance can hide a card the next one shows.
  const nodeCards = cardsFor(section, node.scope);

  const cards = list.map((inst, i) => {
    const path: InstancePath = [...parentPath, { scope: node.scope, index: i }];
    const ctx = ev.byInstance.get(inst.id);
    if (!ctx) return null;
    const summary = node.summaryField ? String(ctx.values[node.summaryField] || '') : '';
    const renderable = (f: FieldDefinition) => renderableIn(f, ctx, cfg, ev);
    const blocks = blocksIn(section.id, nodeCards, nodeFields, '', ctx, renderable, node.children || []);
    return (
      <div className={cardClass} key={inst.id} style={cardStyle}>
        <div className="of-card-head">
          <div className={depth === 0 ? 'of-card-title' : 'of-device-title'} style={depth === 0 ? { fontSize: 18, color: '#2d3748' } : { marginBottom: 0 }}>
            {node.itemLabel} {i + 1}{summary ? ` — ${summary}` : ''}
          </div>
          {canRemove && (
            // "Remove location" is a wide word beside a long title: on a phone
            // the head keeps one line by showing the ✕ instead (form.css).
            // Both are in the DOM at every width, and the aria-label is the
            // sentence either way.
            <button type="button" className={`of-remove${depth > 0 ? ' of-remove-small' : ''}`}
              aria-label={S.remove(node.itemLabel)}
              onClick={() => onState((s) => removeInstance(s, def, path))}>
              <span className="of-remove-label">{S.remove(node.itemLabel)}</span>
              <span className="of-remove-icon" aria-hidden="true">✕</span>
            </button>
          )}
        </div>
        <Blocks
          blocks={blocks} depth={0} ctx={ctx} path={path} instanceId={inst.id} r={r}
          nested={(child) => (
            <WidgetInstances node={child} section={section} parentPath={path} depth={depth + 1} r={r} />
          )}
        />
      </div>
    );
  });

  return (
    <>
      {perRow > 1
        ? <div className="of-inst-grid" style={{ gridTemplateColumns: `repeat(${perRow}, minmax(0, 1fr))` }}>{cards}</div>
        : cards}
      {canAdd && (
        <button type="button" className="of-add"
          onClick={() => onState((s) => addInstance(s, def, cfg, parentPath, node.scope))}>
          {node.addLabel || S.add(node.itemLabel)}
        </button>
      )}
    </>
  );
}
